/* ==========================================================================
   AW26 collection page — Guillo
   All collection-page behaviour: read-more, sticky toolbar offset, view
   toggles, filter/sort drawer with AJAX section rendering, load more,
   card image carousels, desktop quick-size add and the mobile size sheet.

   Cart adds go through the Guillo cart drawer API (window.GuilloCart)
   with a plain /cart/add.js fallback, so Markets pricing, Klaviyo and
   analytics behave exactly as they do everywhere else on the store.
   ========================================================================== */
(function () {
  'use strict';

  var root = null;
  var sectionId = null;
  var collectionUrl = null;
  var openDrawerAfterAdd = true;
  var pendingAdds = new Set();
  var fetchController = null;
  var lastFocused = null;
  var sheetData = null;
  var sheetSibling = null;

  /* ---------- Small utilities ---------- */

  function qs(sel, ctx) { return (ctx || root).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }

  function announce(text) {
    var live = qs('[data-a26-live]');
    if (live) { live.textContent = ''; setTimeout(function () { live.textContent = text; }, 40); }
  }

  var toastEl = null, toastTimer = null;
  function toast(text) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'a26-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 2400);
  }

  function lockScroll(on) {
    document.body.classList.toggle('a26-locked', on);
  }

  function trapFocus(container, e) {
    var focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- Cart ---------- */

  function rawAdd(variantId) {
    return fetch((window.theme && window.theme.routes && window.theme.routes.cart || '/cart') + '/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw j; });
      return r.json();
    });
  }

  function refreshCartUi() {
    if (window.GuilloCart && window.GuilloCart.refresh) {
      window.GuilloCart.refresh();
    } else {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
      }).catch(function () {});
    }
  }

  function addVariant(variantId) {
    if (openDrawerAfterAdd && window.GuilloCart && window.GuilloCart.add) {
      return Promise.resolve(window.GuilloCart.add(variantId, 1));
    }
    return rawAdd(variantId).then(function (res) {
      refreshCartUi();
      return res;
    });
  }

  /* ---------- Quick add (desktop panel + sheet + single-variant) ---------- */

  function handleAddClick(btn) {
    var variantId = parseInt(btn.getAttribute('data-a26-add'), 10);
    if (!variantId || btn.disabled || pendingAdds.has(variantId)) return;

    var inSheet = !!btn.closest('[data-a26-sheet]');
    var msgEl = inSheet
      ? qs('[data-a26-sheet-msg]')
      : (btn.closest('[data-a26-sizes]') ? qs('[data-a26-sizes-msg]', btn.closest('[data-a26-sizes]')) : null);
    if (msgEl) msgEl.textContent = '';

    pendingAdds.add(variantId);
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');

    addVariant(variantId).then(function () {
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      announce('Added to bag');
      if (!openDrawerAfterAdd) toast('Added to bag');
      setTimeout(function () {
        btn.classList.remove('is-added');
        btn.removeAttribute('aria-disabled');
        pendingAdds.delete(variantId);
      }, 1000);
      if (inSheet) setTimeout(closeSheet, openDrawerAfterAdd ? 250 : 700);
    }).catch(function () {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
      pendingAdds.delete(variantId);
      if (msgEl) msgEl.textContent = 'Couldn’t add. Try again.';
      announce('Could not add to bag. Please try again.');
    });
  }

  /* ---------- Card image carousel ---------- */

  function hydrateCard(card) {
    qsa('img[data-a26-src]', card).forEach(function (img) {
      img.src = img.getAttribute('data-a26-src');
      img.srcset = img.getAttribute('data-a26-srcset') || '';
      img.removeAttribute('data-a26-src');
      img.removeAttribute('data-a26-srcset');
    });
  }

  function goToSlide(card, index, behavior) {
    var track = qs('[data-a26-track]', card);
    if (!track) return;
    var slides = qsa('[data-a26-slide]', card);
    var target = slides[index] ? slides[index].offsetLeft : index * track.clientWidth;
    track.scrollTo({ left: target, behavior: behavior || 'smooth' });
    updateDots(card, index);
  }

  function slideCard(card, dir) {
    hydrateCard(card);
    var track = qs('[data-a26-track]', card);
    if (!track) return;
    var count = qsa('[data-a26-slide]', card).length;
    var index = Math.round(track.scrollLeft / track.clientWidth) + dir;
    if (index < 0) index = count - 1;
    if (index > count - 1) index = 0;
    goToSlide(card, index);
  }

  // Desktop hover: reveal the second product image, restore the first on leave
  var hoverSwapMq = window.matchMedia('(hover: hover) and (min-width: 990px)');

  // Preload each card's second image shortly before it can be hovered, so the
  // hover swap is instant instead of waiting on the network. Desktop only —
  // touch devices keep on-demand loading to save data.
  var prefetchObserver = null;

  function hydrateSecondImage(card) {
    var slides = qsa('[data-a26-slide]', card);
    if (slides.length < 2) return;
    var img = qs('img[data-a26-src]', slides[1]);
    if (!img) return;
    img.loading = 'eager';
    img.src = img.getAttribute('data-a26-src');
    img.srcset = img.getAttribute('data-a26-srcset') || '';
    img.removeAttribute('data-a26-src');
    img.removeAttribute('data-a26-srcset');
  }

  function observeCards() {
    if (!hoverSwapMq.matches || !('IntersectionObserver' in window)) return;
    if (!prefetchObserver) {
      prefetchObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          prefetchObserver.unobserve(en.target);
          hydrateSecondImage(en.target);
        });
      }, { rootMargin: '500px 0px' });
    }
    qsa('[data-a26-card]').forEach(function (card) {
      if (card.__a26Observed) return;
      card.__a26Observed = true;
      prefetchObserver.observe(card);
    });
  }

  function onCardEnter(card) {
    if (card.__a26Hover) return;
    card.__a26Hover = true;
    hydrateCard(card);
    if (!hoverSwapMq.matches) return;
    if (qsa('[data-a26-slide]', card).length < 2) return;
    goToSlide(card, 1, 'auto');
  }

  function onCardLeave(card) {
    if (!card.__a26Hover) return;
    card.__a26Hover = false;
    if (!hoverSwapMq.matches) return;
    if (qsa('[data-a26-slide]', card).length < 2) return;
    goToSlide(card, 0, 'auto');
  }

  function updateDots(card, index) {
    qsa('.a26-card__dot', card).forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
    });
  }

  function onTrackScroll(e) {
    var track = e.target;
    if (!track || !track.hasAttribute || !track.hasAttribute('data-a26-track')) return;
    var card = track.closest('[data-a26-card]');
    if (!card) return;
    if (track.__a26raf) return;
    track.__a26raf = requestAnimationFrame(function () {
      track.__a26raf = null;
      updateDots(card, Math.round(track.scrollLeft / track.clientWidth));
    });
  }

  /* ---------- Read more ---------- */

  function initReadMore() {
    var wrap = qs('[data-a26-desc]');
    if (!wrap) return;
    var text = qs('[data-a26-desc-text]', wrap);
    var btn = qs('[data-a26-read-more]', wrap);
    if (!text || !btn) return;
    var overflowing = text.scrollHeight > text.clientHeight + 2;
    btn.hidden = !overflowing;
    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      text.classList.toggle('is-clamped', expanded);
      btn.textContent = expanded ? btn.getAttribute('data-more') : btn.getAttribute('data-less');
    });
  }

  /* ---------- Sticky toolbar offset ---------- */

  function initStickyToolbar() {
    var toolbar = qs('[data-a26-toolbar]');
    if (!toolbar) return;
    var gheader = document.querySelector('.gheader');
    var header = (gheader && gheader.offsetParent !== null ? gheader : null)
      || document.querySelector('.theme__header')
      || document.querySelector('[data-header-wrapper]');
    var ticking = false;
    function update() {
      ticking = false;
      var top = 0;
      if (header) {
        var hb = header.getBoundingClientRect().bottom;
        if (hb > 0 && hb < 240) top = Math.round(hb);
      }
      root.style.setProperty('--a26-stick', top + 'px');
      toolbar.classList.toggle('is-stuck', toolbar.getBoundingClientRect().top <= top + 1 && window.scrollY > 10);
    }
    function requestUpdate() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', update);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    update();
  }

  /* ---------- View toggles ---------- */

  function markViewButtons() {
    var d = getComputedStyle(root).getPropertyValue('--a26-cols-d').trim();
    var m = getComputedStyle(root).getPropertyValue('--a26-cols-m').trim();
    qsa('[data-a26-cols-d]').forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-a26-cols-d') === d); });
    qsa('[data-a26-cols-m]').forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-a26-cols-m') === m); });
  }

  function initViewToggle() {
    try {
      var savedD = localStorage.getItem('a26-cols-d');
      var savedM = localStorage.getItem('a26-cols-m');
      if (savedD) root.style.setProperty('--a26-cols-d', savedD);
      if (savedM) root.style.setProperty('--a26-cols-m', savedM);
    } catch (e) {}
    markViewButtons();
  }

  /* ---------- Filter & sort drawer ---------- */

  function drawerEl() { return qs('[data-a26-drawer]'); }

  function openDrawer(trigger) {
    var d = drawerEl();
    if (!d) return;
    lastFocused = trigger || document.activeElement;
    d.hidden = false;
    requestAnimationFrame(function () { d.classList.add('is-open'); });
    lockScroll(true);
    var openBtn = qs('[data-a26-drawer-open]');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    setTimeout(function () {
      var close = qs('.a26-drawer__close', d);
      if (close) close.focus();
    }, 120);
  }

  function closeDrawer() {
    var d = drawerEl();
    if (!d || d.hidden) return;
    d.classList.remove('is-open');
    lockScroll(false);
    var openBtn = qs('[data-a26-drawer-open]');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    setTimeout(function () { d.hidden = true; }, 300);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ---------- AJAX facets / section rendering ---------- */

  function buildFacetUrl() {
    var form = qs('[data-a26-facets-form]');
    var params = new URLSearchParams();
    if (form) {
      var fd = new FormData(form);
      fd.forEach(function (value, key) {
        if (String(value).trim() !== '') params.append(key, value);
      });
    }
    return collectionUrl + '?' + params.toString();
  }

  function applyUrl(url, opts) {
    opts = opts || {};
    if (fetchController) fetchController.abort();
    fetchController = new AbortController();

    var zone = qs('[data-a26-grid-zone]');
    if (zone) zone.setAttribute('aria-busy', 'true');

    var fetchUrl = new URL(url, location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);

    fetch(fetchUrl.toString(), { signal: fetchController.signal })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        swapResults(doc);
        if (opts.push !== false) {
          var clean = new URL(url, location.origin);
          clean.searchParams.delete('section_id');
          history.pushState({ a26: true }, '', clean.toString());
        }
        var count = qs('[data-a26-count]');
        announce(count ? count.textContent.trim() : 'Results updated');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Network hiccup: fall back to a full navigation so the customer is never stuck
        location.href = url;
      })
      .finally(function () {
        var z = qs('[data-a26-grid-zone]');
        if (z) z.setAttribute('aria-busy', 'false');
      });
  }

  function swapResults(doc) {
    var openKeys = qsa('[data-a26-facet][open]').map(function (f) { return f.getAttribute('data-a26-facet-key'); });
    var focusedInput = document.activeElement && document.activeElement.matches && document.activeElement.matches('[data-a26-facets] input')
      ? { name: document.activeElement.name, value: document.activeElement.value } : null;

    [
      '[data-a26-grid-zone]',
      '[data-a26-chips]',
      '[data-a26-facets]'
    ].forEach(function (sel) {
      var cur = qs(sel);
      var next = doc.querySelector(sel);
      if (cur && next) cur.replaceWith(next);
    });

    // Text/count updates
    [['[data-a26-count]', 'textContent'], ['[data-a26-drawer-count]', 'textContent']].forEach(function (pair) {
      var cur = qs(pair[0]);
      var next = doc.querySelector(pair[0]);
      if (cur && next) cur.textContent = next.textContent;
    });
    var curBadge = qs('[data-a26-active-count]');
    var nextBadge = doc.querySelector('[data-a26-active-count]');
    if (curBadge && nextBadge) {
      curBadge.textContent = nextBadge.textContent;
      curBadge.hidden = nextBadge.hidden;
    }

    // Restore accordion open state
    if (openKeys.length) {
      qsa('[data-a26-facet]').forEach(function (f) {
        var isOpen = openKeys.indexOf(f.getAttribute('data-a26-facet-key')) !== -1;
        if (isOpen) f.setAttribute('open', '');
        else f.removeAttribute('open');
        var toggle = qs('[data-a26-facet-toggle]', f);
        if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
      });
    }

    // Restore focus into the drawer if the customer was filtering
    if (focusedInput) {
      var again = qs('[data-a26-facets] input[name="' + CSS.escape(focusedInput.name) + '"][value="' + CSS.escape(focusedInput.value) + '"]')
        || qs('[data-a26-facets] input[name="' + CSS.escape(focusedInput.name) + '"]');
      if (again) again.focus({ preventScroll: true });
    }

    markViewButtons();
    observeCards();
  }

  var priceTimer = null;
  function onFacetChange(input) {
    if (input.type === 'number') {
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () { applyUrl(buildFacetUrl()); }, 550);
    } else {
      applyUrl(buildFacetUrl());
    }
  }

  /* ---------- Load more ---------- */

  function loadMore(btn) {
    var url = btn.getAttribute('data-next-url');
    if (!url || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    var fetchUrl = new URL(url, location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);

    fetch(fetchUrl.toString())
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newItems = doc.querySelectorAll('[data-a26-grid] > li');
        var grid = qs('[data-a26-grid]');
        var added = 0;
        newItems.forEach(function (li) { grid.appendChild(li); added++; });

        var curMore = qs('[data-a26-more]');
        var nextMore = doc.querySelector('[data-a26-more]');
        if (curMore && nextMore) curMore.replaceWith(nextMore);

        var clean = new URL(url, location.origin);
        clean.searchParams.delete('section_id');
        history.replaceState({ a26: true }, '', clean.toString());
        observeCards();
        announce(added + ' more products loaded');
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        toast('Couldn’t load more. Try again.');
      });
  }

  /* ---------- Mobile quick-size sheet ---------- */

  function sheetEl() { return qs('[data-a26-sheet]'); }

  function sizeButtonsMarkup(variants) {
    var std = variants.filter(function (v) { return !v.long; });
    var lng = variants.filter(function (v) { return v.long; });
    function rows(list) {
      return '<div class="a26-sheet__sizes-grid">' + list.map(function (v) {
        return '<button type="button" class="a26-size-btn" data-a26-add="' + v.id + '"' +
          (v.available ? '' : ' disabled aria-disabled="true"') +
          ' aria-label="' + v.label + (v.long ? ' Long' : '') + (v.available ? '' : ' — sold out') + '">' +
          '<span class="a26-size-btn__label">' + v.label + '</span></button>';
      }).join('') + '</div>';
    }
    var out = '';
    if (lng.length) {
      out += '<p class="a26-sheet__label">Standard length</p>' + rows(std);
      out += '<p class="a26-sheet__label">Long length</p>' + rows(lng);
    } else {
      out += '<p class="a26-sheet__label">Select size</p>' + rows(std);
    }
    return out;
  }

  function renderSheet(sib) {
    var s = sheetEl();
    if (!s) return;
    sheetSibling = sib;
    var thumb = qs('[data-a26-sheet-thumb]', s);
    thumb.src = sib.image || '';
    thumb.alt = sheetData.title;
    qs('[data-a26-sheet-title]', s).textContent = sheetData.title;
    qs('[data-a26-sheet-colour]', s).textContent = sib.name || '';
    qs('[data-a26-sheet-price]', s).textContent = sib.price;
    qs('[data-a26-sheet-sizes]', s).innerHTML = sizeButtonsMarkup(sib.variants);
    qs('[data-a26-sheet-msg]', s).textContent = '';

    var fit = qs('[data-a26-sheet-fit]', s);
    if (fit) {
      var showFit = sib.current && sheetData.fitNote;
      fit.hidden = !showFit;
      fit.textContent = showFit ? sheetData.fitNote : '';
    }

    var coloursWrap = qs('[data-a26-sheet-colours]', s);
    var swatches = qs('[data-a26-sheet-swatches]', s);
    if (coloursWrap && swatches) {
      var sibs = (sheetData.siblings || []).filter(function (x) { return x.available || x.current; });
      if (sibs.length > 1) {
        coloursWrap.hidden = false;
        swatches.innerHTML = sibs.map(function (x) {
          return '<button type="button" class="a26-sheet__swatch' + (x.handle === sib.handle ? ' is-current' : '') + '" data-a26-sheet-colour-pick="' + x.handle + '"' + (x.handle === sib.handle ? ' aria-current="true"' : '') + '>' +
            '<span class="a26-swatch__dot" style="' + (x.hex ? 'background:' + x.hex : 'background-image:url(' + (x.image || '') + ')') + '"></span>' +
            '<span>' + x.name + '</span></button>';
        }).join('');
      } else {
        coloursWrap.hidden = true;
        swatches.innerHTML = '';
      }
    }
  }

  function openSheet(card, trigger) {
    if (!card) return;
    var dataEl = qs('[data-a26-data]', card);
    if (!dataEl) return;
    try { sheetData = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var current = null;
    (sheetData.siblings || []).forEach(function (x) { if (x.current) current = x; });
    if (!current) {
      current = {
        handle: '__self', url: sheetData.url, name: sheetData.colour, hex: null,
        image: sheetData.image, price: sheetData.price, available: true,
        current: true, variants: sheetData.variants
      };
    }
    renderSheet(current);

    var s = sheetEl();
    lastFocused = trigger || document.activeElement;
    s.hidden = false;
    requestAnimationFrame(function () { s.classList.add('is-open'); });
    lockScroll(true);
    setTimeout(function () {
      var close = qs('.a26-sheet__close', s);
      if (close) close.focus();
    }, 120);
  }

  function closeSheet() {
    var s = sheetEl();
    if (!s || s.hidden) return;
    s.classList.remove('is-open');
    var panel = qs('.a26-sheet__panel', s);
    if (panel) panel.style.transform = '';
    lockScroll(false);
    setTimeout(function () { s.hidden = true; }, 320);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function initSheetDrag() {
    var s = sheetEl();
    if (!s) return;
    var panel = qs('.a26-sheet__panel', s);
    var grab = qs('[data-a26-sheet-grab]', s);
    if (!panel || !grab) return;
    var startY = 0, delta = 0, dragging = false;

    grab.addEventListener('pointerdown', function (e) {
      dragging = true; startY = e.clientY; delta = 0;
      panel.style.transition = 'none';
      grab.setPointerCapture(e.pointerId);
    });
    grab.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      delta = Math.max(0, e.clientY - startY);
      panel.style.transform = 'translateY(' + delta + 'px)';
    });
    function release() {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = '';
      if (delta > 80) { closeSheet(); }
      else { panel.style.transform = ''; }
    }
    grab.addEventListener('pointerup', release);
    grab.addEventListener('pointercancel', release);
  }

  /* ---------- Wishlist (local, optional) ---------- */

  function initWishlistState() {
    var saved = [];
    try { saved = JSON.parse(localStorage.getItem('a26-wishlist') || '[]'); } catch (e) {}
    qsa('[data-a26-wish]').forEach(function (b) {
      b.classList.toggle('is-active', saved.indexOf(b.getAttribute('data-product-id')) !== -1);
    });
  }

  function toggleWishlist(btn) {
    var id = btn.getAttribute('data-product-id');
    var saved = [];
    try { saved = JSON.parse(localStorage.getItem('a26-wishlist') || '[]'); } catch (e) {}
    var idx = saved.indexOf(id);
    if (idx === -1) saved.push(id); else saved.splice(idx, 1);
    try { localStorage.setItem('a26-wishlist', JSON.stringify(saved)); } catch (e) {}
    btn.classList.toggle('is-active', idx === -1);
  }

  /* ---------- Event delegation ---------- */

  function onClick(e) {
    var t = e.target;

    var addBtn = t.closest('[data-a26-add]');
    if (addBtn && (root.contains(addBtn) || addBtn.closest('[data-a26-sheet]'))) {
      e.preventDefault(); e.stopPropagation();
      handleAddClick(addBtn);
      return;
    }

    var arrow = t.closest('[data-a26-arrow]');
    if (arrow && root.contains(arrow)) {
      e.preventDefault(); e.stopPropagation();
      slideCard(arrow.closest('[data-a26-card]'), parseInt(arrow.getAttribute('data-a26-arrow'), 10));
      return;
    }

    var sheetOpen = t.closest('[data-a26-sheet-open]');
    if (sheetOpen && root.contains(sheetOpen)) {
      e.preventDefault(); e.stopPropagation();
      openSheet(sheetOpen.closest('[data-a26-card]'), sheetOpen);
      return;
    }

    var colourPick = t.closest('[data-a26-sheet-colour-pick]');
    if (colourPick) {
      e.preventDefault();
      var handle = colourPick.getAttribute('data-a26-sheet-colour-pick');
      var next = (sheetData.siblings || []).filter(function (x) { return x.handle === handle; })[0];
      if (next) renderSheet(next);
      return;
    }

    if (t.closest('[data-a26-sheet-close]')) { e.preventDefault(); closeSheet(); return; }
    var drawerOpenBtn = t.closest('[data-a26-drawer-open]');
    if (drawerOpenBtn) { e.preventDefault(); openDrawer(drawerOpenBtn); return; }
    if (t.closest('[data-a26-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }

    var facetToggle = t.closest('[data-a26-facet-toggle]');
    if (facetToggle) {
      e.preventDefault();
      var facet = facetToggle.closest('[data-a26-facet]');
      var isOpen = facet.hasAttribute('open');
      if (isOpen) facet.removeAttribute('open'); else facet.setAttribute('open', '');
      facetToggle.setAttribute('aria-expanded', String(!isOpen));
      return;
    }

    var chip = t.closest('[data-a26-chip]');
    if (chip && root.contains(chip)) {
      e.preventDefault();
      applyUrl(chip.getAttribute('href'));
      return;
    }

    var more = t.closest('[data-a26-load-more]');
    if (more && root.contains(more)) { e.preventDefault(); loadMore(more); return; }

    var colsD = t.closest('[data-a26-cols-d]');
    if (colsD) {
      root.style.setProperty('--a26-cols-d', colsD.getAttribute('data-a26-cols-d'));
      try { localStorage.setItem('a26-cols-d', colsD.getAttribute('data-a26-cols-d')); } catch (err) {}
      markViewButtons();
      return;
    }
    var colsM = t.closest('[data-a26-cols-m]');
    if (colsM) {
      root.style.setProperty('--a26-cols-m', colsM.getAttribute('data-a26-cols-m'));
      try { localStorage.setItem('a26-cols-m', colsM.getAttribute('data-a26-cols-m')); } catch (err) {}
      markViewButtons();
      return;
    }

    var wish = t.closest('[data-a26-wish]');
    if (wish) { e.preventDefault(); e.stopPropagation(); toggleWishlist(wish); return; }

    // Backdrop clicks
    if (t.matches && t.matches('.a26-drawer__backdrop')) { closeDrawer(); return; }
    if (t.matches && t.matches('.a26-sheet__backdrop')) { closeSheet(); return; }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      var s = sheetEl();
      if (s && !s.hidden) { closeSheet(); return; }
      var d = drawerEl();
      if (d && !d.hidden) { closeDrawer(); return; }
    }
    if (e.key === 'Tab') {
      var sOpen = sheetEl();
      if (sOpen && !sOpen.hidden) { trapFocus(qs('.a26-sheet__panel', sOpen), e); return; }
      var dOpen = drawerEl();
      if (dOpen && !dOpen.hidden) { trapFocus(qs('.a26-drawer__panel', dOpen), e); return; }
    }
  }

  function onChange(e) {
    var input = e.target;
    if (input.closest && input.closest('[data-a26-facets]')) onFacetChange(input);
  }

  function onPointerOver(e) {
    var card = e.target.closest && e.target.closest('[data-a26-card]');
    if (!card) return;
    if (!card.__a26Hydrated) {
      card.__a26Hydrated = true;
      hydrateCard(card);
    }
    if (e.type === 'pointerover' && e.pointerType !== 'touch') onCardEnter(card);
  }

  function onPointerOut(e) {
    var card = e.target.closest && e.target.closest('[data-a26-card]');
    if (!card) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    onCardLeave(card);
  }

  /* ---------- Init ---------- */

  function init() {
    root = document.querySelector('[data-a26]');
    if (!root || root.__a26Init) return;
    root.__a26Init = true;

    sectionId = root.getAttribute('data-section-id');
    collectionUrl = root.getAttribute('data-collection-url');
    openDrawerAfterAdd = root.getAttribute('data-open-drawer-after-add') !== 'false';

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('change', onChange);
    root.addEventListener('pointerover', onPointerOver, { passive: true });
    root.addEventListener('pointerout', onPointerOut, { passive: true });
    root.addEventListener('touchstart', onPointerOver, { passive: true });
    root.addEventListener('focusin', onPointerOver);
    root.addEventListener('scroll', onTrackScroll, { capture: true, passive: true });

    window.addEventListener('popstate', function () {
      applyUrl(location.href, { push: false });
    });

    initReadMore();
    initStickyToolbar();
    initViewToggle();
    initSheetDrag();
    initWishlistState();
    observeCards();
    if (hoverSwapMq.addEventListener) hoverSwapMq.addEventListener('change', observeCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
